import { ISortOptions, IFAQ } from '../models/common-models';

export const B2C_MAX_CARD_LIMIT = 30;
export const B2B_MAX_ACTIVE_CARD_LIMIT = 50;
export const MAX_TRANSACTABLE_CARD_LIMIT = 10;

export const RESTRICTED_RELOAD_PROGRAM = ['50', '59'];
export const RESTRICTED_ADD_TO_ACCOUNT_PROGRAM = ['08', '49', '60', '69', '79'];

export const imgUrl = {
  host: 'https://assets.thdstatic.com',
  pathname: '/images/v1/hd_giftcard_topsellers/'
};

export const images = [
  'giftcard_crate.png',
  'giftcard_paint_cans.png',
  'giftcard_diamond_plate.png',
  'giftcard_puppy_screwdriver.png',
  'giftcard_tape_measure_bow.png',
  'giftcard_welcome_home_mat.png',
  'giftcard_fathersday_grill.png',
  'giftcard_mom.png',
  'giftcard_thankyou_pipe.png',
  'giftcard_birthday_cupcake.png',
  'giftcard_storefront.png',
  'giftcard_made_for_each_other_sketch.png'
];

export const defaultImage = `${imgUrl.host}${imgUrl.pathname}${images[0]}`;

// eslint-disable-next-line max-len
export const INACTIVE_CARD_TOOLTIP_COPY = 'This card will not appear in online checkout as a payment option. To enable it for digital transactions, go to Edit in the menu [•••] and check the box';

export const PAGE_SIZE: number = 9;
export const TRANSACTABLE_PAGE_SIZE: number = 10;

export const SORT_TYPES: ISortOptions = {
  'Date modified: Newest - Oldest': {
    sortBy: 'lastModifiedDt',
    asc: false
  },
  'Date modified: Oldest - Newest': { sortBy: 'lastModifiedDt', asc: true },
  'Name: A - Z': { sortBy: 'nickname', asc: true },
  'Name: Z - A': { sortBy: 'nickname', asc: false },
  'My Active Cards': { sortBy: 'status', asc: false }
};

export const GIFT_CARD_ERRORS = { BALANCE_RECAPTCHA: 'RECAPTCHA_ERR_202', UNAUTH: 'UNAUTHORIZED' };

export const DEFAULT_GIFT_CARD_NAME = 'Home Depot Gift Card';
export const SESSION_EXPIRY_MESSAGE = 'This session will expire in 10 minutes.';

export const OTP_OPTIONS = {
  EMAIL: 'email',
  SMS: 'sms',
  PHONE: 'phone'
};

export const OTP_ERROR_MESSAGES = {
  GENERAL_FAILURE: 'There was a temporary technical issue. Please try again.'
};

export const OTP_ERROR_CODES = {
  MAX_ATTEMPTS_EXCEEDED: ['IAM_ERR_005', 'IAM_ERR_006', 'IAM_ERR_009', 'CUST_AUTH_ERR_119']
};

export const OTP_ERROR_SOURCES = {
  OPTIONS: 'options',
  CODE: 'code',
  VERIFY: 'verify'
} as const;

export type OTPErrorSource = typeof OTP_ERROR_SOURCES[keyof typeof OTP_ERROR_SOURCES];

export const FAQ_CLASSES = {
  BULLET_LIST: 'sui-w-full sui-list-disc sui-px-6 sui-py-4 sui-flex sui-flex-col sui-gap-1',
  DECIMAL_LIST: 'sui-w-full sui-list-decimal sui-px-6 sui-py-4 sui-flex sui-flex-col sui-gap-1'
};

export const FAQ: IFAQ = {
  benefits: {
    title: 'What are the benefits of adding Gift Cards to my Home Depot Account?'
  },
  'adding-giftcard': {
    title: 'Adding Your Gift Card to your Home Depot Account'
  },
  'updating-legacy-giftcard': {
    title: 'Updating Legacy Gift Cards into the New Experience (ProXtra Accounts only)'
  },
  'add-store-credit': {
    title: 'Can I add store credit Gift Cards to my Home Depot Account?'
  },
  'amount-limit': {
    title: 'Is there a limit to how many Gift Cards I can save to my Account?'
  },
  'secure-info': {
    title: 'Is my saved Gift Card information secure?'
  },
  'combine-balance': {
    title: 'Can I combine the balance of multiple gift cards into one card?'
  },
  'account-compromised': {
    title: 'What happens if my Account is compromised? Will I lose my saved Gift Cards?'
  },
  redemption: {
    title: 'Redeeming The Home Depot Gift Card Added to your Home Depot Account'
  },
  'payment-grayed-out': {
    title: 'Why is the Gift Card payment option grayed out at checkout?'
  },
  'reloading-proxtra': {
    title: 'Reloading a Saved Gift Card from your ProXtra Account'
  },
  help: {
    title: 'Still need help?'
  }
};

export const FEATURE_SWITCH_NAMES = {
  reCaptchaEnabled: 'reCaptchaEnabled',
  DISABLE_GIFT_CARD_RELOAD: 'disableGiftCardReload',
  enableReloadB2c: 'fs-prop:payments__enableReloadGiftCardB2c',
  DISABLE_GIFT_CARD_CHECK_BALANCE_AND_RELOAD: 'disableGiftCardCheckbalanceAndReload',
  AUTH_EVENT_FILTER_HOURS: 'AUTH_EVENT_FILTER_HOURS',
  ENABLE_SCAN_TO_PAY_B2B: 'enableScanToPayB2B',
  ENABLE_SCAN_TO_PAY_B2C: 'enableScanToPayB2C',
  GIFT_CARD_BARCODE_TIMEOUT: 'giftCardBarcodeTimeout',
  SCAN_TO_PAY_STORE_ID_LIST: 'scanToPayStoreIdList',
  SCAN_TO_PAY_REQUIRE_IN_STORE: 'scanToPayRequireInStore'
};

export const DEFAULT_AUTH_EVENT_FILTER_HOURS = 48;
export const DEFAULT_GIFT_CARD_BARCODE_TIMEOUT = 10;
export const DEFAULT_GIFT_CARD_BARCODE_TIMEOUT_MESSAGE = 'This session will expire in 10 minutes.';

export const AUTHENTICATION_EVENTS = {
  SIGNED_IN_PASSWORD: 'EVT000001',
  SIGNED_IN_BIOMETRIC: 'EVT000002',
  SIGNED_IN_OTP: 'EVT000003',
  SIGNED_IN_OTP_FORGOT_PASSWORD: 'EVT000004',
  SIGNED_IN_2FA: 'EVT000005',
  MULTI_ACCOUNT_LOGIN: 'EVT000006',
  REGISTER_AND_SIGNED_IN: 'EVT000007',
  PASSWORD_AND_2FA: 'EVT000008',
  SIGNED_OUT: 'EVT000009',
  SIGNED_OUT_ALL_DEVICES: 'EVT000010',
  ENROLLED_PASSWORDLESS_AUTHENTICATION: 'EVT000011',
  ENROLLED_BIOMETRIC_AUTHENTICATION: 'EVT000012',
  UNENROLLED_PASSWORDLESS_AUTHENTICATION: 'EVT000013',
  PASSWORD_UPDATED: 'EVT000014'
};

export const SECURE_AUTH_EVENT_IDS = [
  AUTHENTICATION_EVENTS.SIGNED_IN_2FA,
  AUTHENTICATION_EVENTS.SIGNED_IN_OTP,
  AUTHENTICATION_EVENTS.SIGNED_IN_BIOMETRIC,
  AUTHENTICATION_EVENTS.SIGNED_IN_OTP_FORGOT_PASSWORD,
  AUTHENTICATION_EVENTS.PASSWORD_AND_2FA
];

// TODO: Remove these development/testing cookie names before enabling feature switches for customers
export const DEV_TESTING_COOKIES = {
  SCAN_TO_PAY_IN_STORE: 'scanToPayInStore',
  BYPASS_OTP_FLOW: 'bypassOTPFlow'
};
